import type { Manifest, ManifestSummary } from '../../shared/manifest-types';
export const CLIENT_DASHBOARD_BASE = 'https://dashboard.sheahanstudios.com';
export interface WaitingFlag {
  key: string;
  label: string;
  days: number;
  tier: 3 | 7 | 14;
  reminded_at: string | null;
  next_allowed_at?: string | number | null;
}
export interface DashboardVisits {
  at: string;
  count: number;
}
export type CommsSummary = ManifestSummary & {
  waiting_flags?: WaitingFlag[];
  last_viewed?: DashboardVisits | null;
  last_digest_sent_at?: string | null;
};
export type EditorManifest = Manifest & {
  _etag?: string | null;
};
export interface DigestDraft {
  subject: string;
  body_text: string;
  events: {
    date: string;
    label: string;
    meta?: string;
  }[];
  last_digest_sent_at: string | null;
  recipient: string | null;
  client_name: string;
  sender_configured?: boolean;
}
export interface WaitingData {
  flags: WaitingFlag[];
  last_viewed: DashboardVisits | null;
  recipient: string | null;
  client_name: string;
  last_digest_sent_at: string | null;
  sender_configured?: boolean;
}
export interface EmailDraft {
  subject: string;
  body_text: string;
  request_id?: string;
}
export const REMINDER_PAUSE_MS = 6 * 60 * 60 * 1000;
export function reminderAvailableAt(at: string | null): number | null {
  const time = at ? Date.parse(at) : NaN;
  return Number.isFinite(time) ? time + REMINDER_PAUSE_MS : null;
}
export function reminderDeadline(remindedAt: string | null, nextAllowedAt?: string | number | null): number | null {
  const supplied = typeof nextAllowedAt === 'number' ? nextAllowedAt : nextAllowedAt ? Date.parse(nextAllowedAt) : NaN;
  return Number.isFinite(supplied) ? supplied : reminderAvailableAt(remindedAt);
}
export function reminderIsPaused(at: string | null, now = Date.now()): boolean {
  const until = reminderAvailableAt(at);
  return until !== null && until > now;
}
export function dashboardPreviewUrl(slug: string): string { return `${CLIENT_DASHBOARD_BASE}/${encodeURIComponent(slug)}?internal=1`; }
export function commsTimestamp(value: string | null | undefined): string {
  if (!value || Number.isNaN(Date.parse(value)))
    return '—';
  return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}
export function reviewRoundChoices(manifest: Manifest): number[] {
  const rounds = manifest.review?.rounds?.map(r => r.round).filter(n => Number.isSafeInteger(n) && n > 0) ?? [];
  return [...new Set([...rounds, Math.max(0, ...rounds) + 1])].sort((a, b) => a - b);
}
export function initialReviewRound(manifest: Manifest): number {
  const unfinished = manifest.review?.rounds?.filter(r => r.status !== 'approved').map(r => r.round).filter(n => Number.isSafeInteger(n) && n > 0) ?? [];
  return unfinished.length ? Math.max(...unfinished) : Math.max(0, ...reviewRoundChoices(manifest).slice(0, -1)) + 1;
}
export function reviewEntryPayload(kind: 'review' | 'final', fields: {
  title: string;
  url: string;
  extra: string;
  notify: boolean;
}) {
  const title = fields.title.trim(), url = fields.url.trim();
  if (!title)
    throw new Error('Enter a title.');
  let parsed: URL;
  try {
    parsed = new URL(url);
  }
  catch {
    throw new Error('Enter a valid HTTP or HTTPS URL.');
  }
  if (!['http:', 'https:'].includes(parsed.protocol))
    throw new Error('Enter a valid HTTP or HTTPS URL.');
  return { title, url, notify: fields.notify, ...(fields.extra.trim() ? { [kind === 'review' ? 'platform' : 'format']: fields.extra.trim() } : {}) };
}
export function reminderDraft(client: string, slug: string, flag: WaitingFlag): EmailDraft {
  return { subject: `A quick check-in on ${flag.label.toLowerCase()}`, body_text: `Hi ${client},\n\nJust checking in on ${flag.label.toLowerCase()}. If you have any questions or need a hand, reply here and we can work through it together.\n\nYour project dashboard:\n${CLIENT_DASHBOARD_BASE}/${encodeURIComponent(slug)}\n\nThanks,\nHenry` };
}
const draftKey = (key: string) => `ssos:client-comms-draft:v1:${key}`;
export function readEmailDraft(key: string): EmailDraft | null {
  try {
    const d = JSON.parse(sessionStorage.getItem(draftKey(key)) ?? 'null');
    return d && typeof d.subject === 'string' && typeof d.body_text === 'string' ? { subject: d.subject, body_text: d.body_text, ...(typeof d.request_id === 'string' ? { request_id: d.request_id } : {}) } : null;
  }
  catch {
    return null;
  }
}
export function writeEmailDraft(key: string, draft: EmailDraft | null): void {
  try {
    if (draft)
      sessionStorage.setItem(draftKey(key), JSON.stringify({ subject: draft.subject, body_text: draft.body_text, ...(draft.request_id ? { request_id: draft.request_id } : {}) }));
    else
      sessionStorage.removeItem(draftKey(key));
  }
  catch { /* Quota/private-mode failure must not stop composing. */ }
}
/** Preserve only edits made after a submitted snapshot while establishing a fresh baseline. */
export function retainLaterEdits<T extends object>(submitted: T, live: T, published: T): T {
  const next = { ...published };
  for (const k of Object.keys(submitted) as (keyof T)[])
    if (JSON.stringify(live[k]) !== JSON.stringify(submitted[k]))
      next[k] = live[k];
  return next;
}
